import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PerkData, PerkFilterType } from '@cobu/types';
import { getPerks, getSavedPerks, getReferralOffer } from '@cobu/api-client';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../utils/logger';

interface UsePerksReturn {
  currentFilter: PerkFilterType;
  refreshing: boolean;
  perks: PerkData[];
  isLoading: boolean;
  error: string | null;
  handleFilterPress: (selectedFilter: PerkFilterType) => void;
  onRefresh: () => Promise<void>;
  onPressListItem: (perkId: string, heroColor?: string) => void;
}

export const usePerks = (): UsePerksReturn => {
  const [currentFilter, setCurrentFilter] = useState<PerkFilterType>('all');
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  // Get user and building info from Redux state
  const user = useSelector((state: RootState) => state.auth);
  const buildingId = (user as any)?.user?.building?.uuid || (user as any)?.user?.buildingId || '';

  const userId = (user as any)?.user?.uuid || (user as any)?.user?.id || '';

  // Fetch data using React Query hooks
  const { data: allPerks = [], isLoading: allPerksLoading, error: allPerksError } = useQuery({
    queryKey: ['perks', userId],
    queryFn: () => getPerks(userId),
    enabled: !!userId,
  });

  const {
    data: savedPerks = [],
    isLoading: savedPerksLoading,
    error: savedPerksError,
  } = useQuery({
    queryKey: ['savedPerks', userId],
    queryFn: () => getSavedPerks(userId),
    enabled: !!userId,
  });

  // Fetch referral offer for the building
  const {
    data: referralOffer,
    isLoading: isReferralOfferLoading,
    error: referralOfferError,
  } = useQuery({
    queryKey: ['referralOffer', buildingId || ''],
    queryFn: () => getReferralOffer(buildingId || ''),
    enabled: !!buildingId,
  });

  const queryClient = useQueryClient();
  const refreshPerks = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['perks'] }),
      queryClient.invalidateQueries({ queryKey: ['savedPerks'] }),
      queryClient.invalidateQueries({ queryKey: ['referralOffer'] }),
    ]);
  }, [queryClient]);

  // Determine current perks based on filter
  const perks = useMemo(() => {
    const basePerks = currentFilter === 'all' ? allPerks : savedPerks;

    if (referralOffer) {
      const referralPerk: PerkData = {
        uuid: referralOffer.uuid,
        name: referralOffer.title,
        bannerTitle: referralOffer.bannerTitle,
        isReferral: true,
      };

      return [referralPerk, ...basePerks];
    }

    return basePerks;
  }, [currentFilter, allPerks, savedPerks, referralOffer]);

  // Loading state (show loading when fetching the current filter's data)
  const isLoading = useMemo(() => {
    if (currentFilter === 'all') {
      return allPerksLoading || isReferralOfferLoading;
    }
    return savedPerksLoading || isReferralOfferLoading;
  }, [currentFilter, allPerksLoading, savedPerksLoading, isReferralOfferLoading]);

  // Error state (show error from current filter's data)
  const error = useMemo(() => {
    if (currentFilter === 'all') {
      return allPerksError?.message || referralOfferError?.message || null;
    }
    return savedPerksError?.message || referralOfferError?.message || null;
  }, [currentFilter, allPerksError, savedPerksError, referralOfferError]);

  const handleFilterPress = useCallback(
    (selectedFilter: PerkFilterType) => {
      if (selectedFilter === currentFilter) {
        return;
      }
      setCurrentFilter(selectedFilter);
      /* ANALYTICS_DISABLED: SELECT_PERK_FILTER ('select_perk_filter')
          analyticsService.trackEvent(AnalyticsEvent.SELECT_PERK_FILTER, { filter: selectedFilter });
          */
    },
    [currentFilter]
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshPerks();
      /* ANALYTICS_DISABLED: SEARCH_PERK_FEED ('search_perk_feed')
        analyticsService.trackEvent(AnalyticsEvent.SEARCH_PERK_FEED, { action: 'refresh' });
        */
    } catch (error) {
      logger.error('Error refreshing perks:', error);
    } finally {
      setRefreshing(false);
    }
  }, [refreshPerks]);

  const onReferralOfferPress = useCallback((perkId: string) => {
    navigate('/perks/referral');
  }, [navigate]);

  const onPerkPress = useCallback((perkId: string, heroColor?: string) => {
    analyticsService.trackEvent(AnalyticsEvent.VIEW_PERK_DETAIL, { perkId });
    navigate(`/perks/${perkId}`, { state: { heroColor } });
  }, [navigate]);


  const onPressListItem = useCallback((perkId: string, heroColor?: string) => {
    if (perkId === referralOffer?.uuid) {
      onReferralOfferPress(perkId);
    } else {
      onPerkPress(perkId, heroColor);
    }
  }, [referralOffer, onReferralOfferPress, onPerkPress]);

  return {
    currentFilter,
    refreshing,
    perks,
    isLoading,
    error,
    handleFilterPress,
    onRefresh,
    onPressListItem,
  };
};
